import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchSearch, fetchProgress } from "@/lib/api";
import { compoundUnlockState } from "@/lib/unlock";
import UnlockBadge from "@/components/UnlockBadge";
import JlptBadge from "@/components/JlptBadge";
import { TEST_IDS } from "@/constants/testIds";

export default function SearchBar({ onSelect, onSelectWord, placeholder = "search kanji, compound, meaning…", className = "" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState({ kanji: [], vocab: [] });
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const progQ = useQuery({ queryKey: ["progress"], queryFn: fetchProgress });

  useEffect(() => {
    if (!q.trim()) { setResult({ kanji: [], vocab: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { setResult(await fetchSearch(q)); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const fn = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const totalCount = (result.kanji?.length || 0) + (result.vocab?.length || 0);
  const close = () => { setOpen(false); setQ(""); };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="flex items-center border border-borderDefault bg-washi/80 backdrop-blur-sm px-3 py-1.5 gap-2 w-44 md:w-72">
        <Search size={14} className="text-sumiSoft" />
        <input
          data-testid={TEST_IDS.searchInput}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          lang="ja"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className="bg-transparent outline-none text-sm font-sans flex-1 placeholder:text-sumiSoft/70 text-sumi min-w-0"
        />
      </div>
      {open && q && (
        <div
          data-testid={TEST_IDS.searchResults}
          className="absolute right-0 mt-1 w-96 max-h-[60vh] overflow-y-auto bg-washi border border-borderDefault z-50"
        >
          {loading && <p className="px-3 py-2 text-xs text-sumiSoft font-sans">searching…</p>}
          {!loading && totalCount === 0 && (
            <p className="px-3 py-2 text-xs text-sumiSoft font-sans">no matches</p>
          )}
          {result.kanji?.length > 0 && (
            <>
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">kanji</p>
              {result.kanji.map((k) => (
                <button
                  key={k.kanji}
                  data-testid={TEST_IDS.searchResultItem(k.kanji)}
                  onClick={() => { onSelect?.(k.kanji); close(); }}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-washiPanel border-b border-borderDefault last:border-b-0 text-left"
                >
                  <span className="font-serif text-2xl text-sumi">{k.kanji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm text-sumi truncate">{k.meaning}</p>
                    <p className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                      {k.on_readings?.[0] || ""}
                    </p>
                  </div>
                  <JlptBadge level={k.jlpt} jouyou={k.jouyou} size="sm" />
                </button>
              ))}
            </>
          )}
          {result.vocab?.length > 0 && (
            <>
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans border-t border-borderDefault">compounds</p>
              {result.vocab.map((v) => {
                const unlock = compoundUnlockState(Array.from(v.word), progQ.data?.items);
                return (
                <button
                  key={`${v.word}-${v.reading}`}
                  data-testid={`search-word-${v.word}`}
                  onClick={() => { (onSelectWord || onSelect)?.(v.word, "word"); close(); }}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-washiPanel border-b border-borderDefault last:border-b-0 text-left"
                >
                  <span className="font-serif text-xl text-sumi">{v.word}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm text-sumi truncate">{v.meaning}</p>
                    <p className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                      {v.reading}
                    </p>
                  </div>
                  <JlptBadge level={v.jlpt} size="sm" />
                  {unlock.total > 0 && (
                    <UnlockBadge state={unlock.state} known={unlock.known} total={unlock.total} variant="dot" />
                  )}
                </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
